/**
 * Simple utility functions for formatting corporate actions data for UI display
 * These are pure helper functions that convert gRPC types to display strings
 */

import { format, DisplayType } from '@/utils/numberFormatter/formatNumbers';
import {
  CorporateActionType,
  CorporateActionStatus,
  type CorporateAction,
  type Timestamp,
} from '@/features/tokenized-equity/grpc/types';
import {
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
  type StatusFilterValue,
  type TypeFilterValue,
} from './corporateActionsConstants';

/**
 * Parse status filter string to CorporateActionStatus enum array
 */
export function parseStatusFilter(
  statusFilter: StatusFilterValue,
): CorporateActionStatus[] | undefined {
  switch (statusFilter) {
    case STATUS_FILTER_OPTIONS.ALL:
      return [
        CorporateActionStatus.CORPORATE_ACTION_STATUS_ANNOUNCED,
        CorporateActionStatus.CORPORATE_ACTION_STATUS_COMPLETED,
      ];
    case STATUS_FILTER_OPTIONS.UPCOMING:
      return [CorporateActionStatus.CORPORATE_ACTION_STATUS_ANNOUNCED];
    case STATUS_FILTER_OPTIONS.COMPLETED:
      return [CorporateActionStatus.CORPORATE_ACTION_STATUS_COMPLETED];
    default:
      return undefined;
  }
}

/**
 * Parse type filter string to CorporateActionType enum array
 */
export function parseTypeFilter(typeFilter: TypeFilterValue): CorporateActionType[] | undefined {
  switch (typeFilter) {
    case TYPE_FILTER_OPTIONS.ALL:
      return undefined;
    case TYPE_FILTER_OPTIONS.CASH_DIVIDEND:
      return [CorporateActionType.CORPORATE_ACTION_TYPE_CASH_DIVIDEND];
    case TYPE_FILTER_OPTIONS.STOCK_DIVIDEND:
      return [CorporateActionType.CORPORATE_ACTION_TYPE_STOCK_DIVIDEND];
    case TYPE_FILTER_OPTIONS.STOCK_SPLIT:
      return [CorporateActionType.CORPORATE_ACTION_TYPE_STOCK_SPLIT];
    case TYPE_FILTER_OPTIONS.REVERSE_STOCK_SPLIT:
      return [CorporateActionType.CORPORATE_ACTION_TYPE_REVERSE_STOCK_SPLIT];
    default:
      return undefined;
  }
}

/**
 * Convert timestamp to formatted date string (MM/DD/YYYY)
 */
export function formatTimestamp(timestamp: Timestamp | undefined): string {
  if (!timestamp?.seconds) return 'N/A';
  const date = new Date(parseInt(timestamp.seconds) * 1000);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

/**
 * Convert action type enum to display string
 */
export function formatActionType(actionType: CorporateActionType): string {
  switch (actionType) {
    case CorporateActionType.CORPORATE_ACTION_TYPE_CASH_DIVIDEND:
      return 'Cash Dividend';
    case CorporateActionType.CORPORATE_ACTION_TYPE_STOCK_DIVIDEND:
      return 'Stock Dividend';
    case CorporateActionType.CORPORATE_ACTION_TYPE_STOCK_SPLIT:
      return 'Stock Split';
    case CorporateActionType.CORPORATE_ACTION_TYPE_REVERSE_STOCK_SPLIT:
      return 'Reverse Stock Split';
    default:
      return 'Unspecified';
  }
}

/**
 * Convert status enum to display string
 */
export function formatStatus(status: CorporateActionStatus): string {
  switch (status) {
    case CorporateActionStatus.CORPORATE_ACTION_STATUS_COMPLETED:
      return 'Completed';
    case CorporateActionStatus.CORPORATE_ACTION_STATUS_ANNOUNCED:
      return 'Upcoming';
    default:
      return 'Unspecified';
  }
}

/**
 * Get the relevant date from corporate action details
 */
export function getActionDate(action: CorporateAction): string {
  const { details } = action;

  if (details.cash_dividend) {
    return formatTimestamp(details.cash_dividend.payout_date);
  }
  if (details.stock_dividend) {
    return formatTimestamp(details.stock_dividend.paid_date);
  }
  if (details.stock_split) {
    return formatTimestamp(details.stock_split.effective_date);
  }
  if (details.reverse_stock_split) {
    return formatTimestamp(details.reverse_stock_split.effective_date);
  }

  return 'N/A';
}

/**
 * Format details value for display
 */
export function formatDetails(action: CorporateAction): string {
  const { details } = action;

  // Cash Dividend: format as "$X.XXX/Share"
  if (details.cash_dividend) {
    const formatted = format({
      value: details.cash_dividend.amount,
      type: DisplayType.Fiat,
      customConfig: {
        format: '$0.000',
      },
    });
    return `${formatted}/Share`;
  }

  // Stock Dividend: format as "+X,XXX Shares"
  if (details.stock_dividend) {
    const formatted = format({
      value: details.stock_dividend.amount,
      type: DisplayType.TokenAmount,
      customConfig: {
        format: '0,0.[0000]',
      },
    });
    return `+${formatted} Shares`;
  }

  // Stock Split: format as "X:Y"
  if (details.stock_split) {
    return details.stock_split.ratio;
  }

  // Reverse Stock Split: format as "X:Y"
  if (details.reverse_stock_split) {
    return details.reverse_stock_split.ratio;
  }

  return 'N/A';
}

/**
 * Get impact type (Cash or Shares)
 */
export function getImpact(action: CorporateAction): string {
  const { details } = action;

  if (details.cash_dividend) return 'Cash';
  if (details.stock_dividend) return 'Shares';
  if (details.stock_split) return 'Shares';
  if (details.reverse_stock_split) return 'Shares';

  return 'N/A';
}
